import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import StyledDropzone from '../StyledDropzone';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import ConfirmationButtonDialog from 'components/helpers/ConfirmationButtonDelete';
import { isTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import { isDirector } from 'components/helpers/IsDirector';

interface FileUploaderProps {
  service: any;
  entityId: number;
  attachmentName: string;
  filename: string | null;
  onUploadSuccess?: any;
  originalService?: string;
  disableEdit?: boolean;
  disableActions?: boolean;
}

function FileUploader(props: FileUploaderProps) {
  const { t } = useTranslation();
  const {
    service,
    entityId,
    attachmentName,
    filename,
    onUploadSuccess,
    originalService,
    disableEdit,
    disableActions
  } = props;

  const [openDropzone, setOpenDropzone] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [tmpCanDownload, setTmpCanDownload] = useState<boolean>(false);
  const [attachmentFilename, setAttachmentFilename] = useState(filename);
  const { enqueueSnackbar } = useSnackbar();
  const isDir = isDirector();

  useEffect(() => {
    setTmpCanDownload(typeof filename === 'string');
  }, []);

  const attachFile = () => {
    if (entityId) {
      setOpenDropzone(true);
    }
  };

  const downloadAttachment = () => {
    if (entityId) {
      service.downloadAttachment(entityId, attachmentName);
    }
  };

  const deleteAttachment = (id?: number) => {
    if (entityId) {
      service.deleteAttachment(entityId, attachmentName);
      setTmpCanDownload(false);
      setAttachmentFilename(t('actions.insert'));
    }
  };

  const handleUpload = () => {
    if (fileToUpload && entityId) {
      service.attachFiles(fileToUpload, attachmentName, entityId).then(() => {
        if (onUploadSuccess) onUploadSuccess();
        setTmpCanDownload(true);
        enqueueSnackbar('Attachment uploaded.', { variant: 'success' });
        setOpenDropzone(false);
      });
    }
  };

  const handleClose = () => {
    setFileToUpload(null);
    setOpenDropzone(false);
  };

  const handleDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) {
      enqueueSnackbar('The file you are trying to upload exceeds 20 MiB.', { variant: 'error' });
      return;
    }

    const file = acceptedFiles[0];
    if (file) {
      setAttachmentFilename(file.name);
      setFileToUpload(file);
    }
  };

  // const onSubmit = (values) => {
  //   //upload document to API and handle response
  //   fmDocumentService
  //     .uploadDocument(
  //       values.category,
  //       values.attachment.item(0),
  //       values.name,
  //       values.description || '',
  //       originalService,
  //       [entityId]
  //     )
  //     .then((res) => {
  //       if (res) {
  //         // if response is successful, show success toast message
  //         toast.success(t('entities.document.upload-success'));
  //       } else {
  //         // if response is unsuccessful, show error toast message
  //         toast.error(t('entities.document.upload-error'));
  //       }
  //     });
  // };

  return (
    <div>
      <div>
        <ButtonGroup variant="outlined" size="small">
          <Button onClick={attachFile} disabled={disableActions || disableEdit || isDir}>
            {typeof filename === 'string' || tmpCanDownload
              ? attachmentFilename
              : t('actions.insert')}
          </Button>
          {tmpCanDownload && (
            <Button onClick={downloadAttachment} color="success">
              <DownloadIcon />
            </Button>
          )}
          {tmpCanDownload && !disableEdit && !isDir && (
            <ConfirmationButtonDialog handleDelete={deleteAttachment} iconButton={true} />
          )}
        </ButtonGroup>
      </div>
      <Dialog open={openDropzone}>
        <DialogTitle>{t(`attachment-modal.title`)}</DialogTitle>
        <DialogContent>
          <StyledDropzone onDrop={handleDrop} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('actions.cancel')}</Button>
          <Button onClick={handleUpload} disabled={!fileToUpload || isDir}>
            {t('actions.upload')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default FileUploader;
